'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import computerImage from '../../public/images/neu-pos/computerphoto.png';
import { motion } from 'framer-motion';

export default function NeuposHero({
    heading = 'Revolutionize Your Business',
    caption = 'Experience the power of modern point-of-sale systems with stunning visual design and seamless functionality.',
    buttonText = 'Get Started',
    buttonLink = '#',

    leftImage = '/api/placeholder/550/700',
    rightImage = '/api/placeholder/550/700',
    centerImage = '/api/placeholder/550/700',
}) {
    const [activeIndex, setActiveIndex] = useState(0);
    const images = [leftImage, centerImage, rightImage];

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [images.length]);

    const getImagePosition = (index) => {
        const diff = (index - activeIndex + images.length) % images.length;

        if (diff === 0) {
            return {
                x: 0,
                y: 0,
                rotate: 0,
                scale: 1.15,
                zIndex: 30,
                opacity: 1,
                filter: 'blur(0px)',
            };
        } else if (diff === 1) {
            return {
                x: 100,
                y: 60,
                rotate: 3,
                scale: 1.05,
                zIndex: 20,
                opacity: 0.9,
                filter: 'blur(0.6px)',
            };
        } else {
            return {
                x: -100,
                y: 60,
                rotate: -3,
                scale: 1.05,
                zIndex: 10,
                opacity: 0.9,
                filter: 'blur(0.6px)',
            };
        }
    };

    return (
        <section className="relative pt-4 pb-2 md:py-4 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-12">
                    {/* LEFT CONTENT */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        className="lg:w-1/2"
                    >
                        <h1 className="hello_text text-2xl md:text-5xl font-bold leading-tight">
                            {heading}
                        </h1>

                        <p className="mt-3 md:mt-4 text-gray-600 text-base md:text-lg font-semibold">
                            {caption}
                        </p>

                        {buttonText && buttonLink && (
                            <a href={buttonLink}>
                                <button className="hello_button mt-4 px-10 py-3 rounded-md transition">
                                    <span className="font-bold text-white text-sm md:text-base">
                                        {buttonText}
                                    </span>
                                </button>
                            </a>
                        )}
                    </motion.div>

                    {/* ---------- MOBILE IMAGE ---------- */}
                    <div className="lg:hidden w-full mt-6">
                        <div className="flex justify-center">
                            <div className="relative w-full max-w-[360px]">
                                {' '}
                                <Image
                                    src={computerImage}
                                    alt="mobile showcase"
                                    width={480}
                                    height={620}
                                    className="w-full h-auto object-contain rounded-2xl"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ---------- DESKTOP IMAGE CLUSTER ---------- */}
                    <div className="hidden lg:flex lg:w-1/2 justify-center items-center -mt-32">
                        <div className="relative w-full max-w-[1000px] h-[750px]">
                            {' '}
                            {images.map((image, index) => {
                                const position = getImagePosition(index);

                                return (
                                    <motion.div
                                        key={index}
                                        animate={position}
                                        transition={{
                                            duration: 0.8,
                                            ease: 'easeInOut',
                                        }}
                                        whileHover={{
                                            scale: position.scale * 1.05,
                                            y: position.y - 10,
                                            zIndex: 50,
                                            boxShadow:
                                                '0 30px 60px rgba(0,0,0,0.3)',
                                            filter: 'blur(0px)',
                                            opacity: 1,
                                        }}
                                        className="absolute left-1/4 top-1/3 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                                        style={{
                                            zIndex: position.zIndex,
                                            filter: position.filter,
                                        }}
                                    >
                                        <Image
                                            src={image}
                                            alt={`showcase ${index + 1}`}
                                            width={820}
                                            height={860}
                                            className="rounded-3xl object-cover border-4 border-white shadow-2xl"
                                        />
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
